import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message as antMessage,
} from "antd";
import {
  CheckOutlined,
  CopyOutlined,
  FileTextOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  PictureOutlined,
  ReloadOutlined,
  SendOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import {
  analyzeAd,
  chatWithAd,
  fileToImagePart,
  isVideoFile,
  runPool,
  setAssignmentWorkspaceId,
  transcribeMedia,
  uploadForTranscription,
} from "../services/assignmentService";
import MasterPromptModal from "./MasterPromptModal";
import { usePasteFiles } from "../hooks/usePasteFiles";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const STATUS_META = {
  queued: { color: "default", label: "Queued" },
  extracting: { color: "processing", label: "Extracting audio" },
  uploading: { color: "processing", label: "Uploading" },
  transcribing: { color: "processing", label: "Transcribing" },
  analyzing: { color: "processing", label: "Analyzing" },
  done: { color: "success", label: "Ready" },
  error: { color: "error", label: "Failed" },
};

const uid = () =>
  `ad_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * A click-to-copy copy block. This is the whole point of the tab: the copy the
 * model assigned needs to get into Ads Manager in one click, not a select-drag.
 */
function CopyField({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      antMessage.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <Text
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            opacity: 0.7,
            fontWeight: 600,
          }}
        >
          {label}
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {value ? `${value.length} chars` : "—"}
        </Text>
      </div>

      <Tooltip title={copied ? "Copied" : "Click to copy"} placement="topLeft">
        <div
          role="button"
          tabIndex={0}
          onClick={copy}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              copy();
            }
          }}
          style={{
            position: "relative",
            cursor: value ? "pointer" : "default",
            padding: "12px 40px 12px 14px",
            borderRadius: 10,
            border: `1px solid ${copied ? "var(--brand-500)" : "var(--app-border)"}`,
            background: "var(--app-surface-2)",
            transition: "border-color .15s ease, background .15s ease",
            minHeight: 46,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.55,
          }}
        >
          {value || <Text type="secondary">Nothing assigned</Text>}
          <span
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              opacity: 0.55,
              fontSize: 13,
            }}
          >
            {copied ? <CheckOutlined style={{ color: "var(--brand-500)" }} /> : <CopyOutlined />}
          </span>
        </div>
      </Tooltip>

      {hint ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      ) : null}
    </div>
  );
}

/** One ad = one isolated channel. */
function ChannelDetail({ channel, onSend, onRetry }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [channel?.messages?.length, channel?.chatBusy]);

  if (!channel) {
    return (
      <Card className="panel-card" style={{ minHeight: 420 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Upload ads, then pick one to see its assigned copy."
        />
      </Card>
    );
  }

  const { result, status, error } = channel;
  const busy = [
    "queued",
    "extracting",
    "uploading",
    "transcribing",
    "analyzing",
  ].includes(status);

  const send = () => {
    const text = draft.trim();
    if (!text || channel.chatBusy) return;
    setDraft("");
    onSend(channel.id, text);
  };

  const copyAll = async () => {
    if (!result) return;
    const block = [
      `HEADLINE\n${result.headline}`,
      `PRIMARY TEXT\n${result.primary_text}`,
      `DESCRIPTION\n${result.description}`,
    ].join("\n\n");
    await navigator.clipboard.writeText(block);
    antMessage.success("All three fields copied.");
  };

  return (
    <Card
      className="panel-card"
      title={
        <Space size={8}>
          {channel.mediaKind === "video" ? <PlayCircleOutlined /> : <PictureOutlined />}
          <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 14 }}>
            {channel.filename}
          </span>
        </Space>
      }
      extra={
        <Space>
          <Tag color={STATUS_META[status]?.color}>{STATUS_META[status]?.label}</Tag>
          {status === "error" ? (
            <Button size="small" icon={<ReloadOutlined />} onClick={() => onRetry(channel.id)}>
              Retry
            </Button>
          ) : null}
          {result ? (
            <Button size="small" type="primary" ghost icon={<CopyOutlined />} onClick={copyAll}>
              Copy all
            </Button>
          ) : null}
        </Space>
      }
    >
      {status === "error" ? (
        <Alert
          type="error"
          showIcon
          message="Analysis failed"
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {busy ? (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <Spin />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">
              {status === "extracting"
                ? `Stripping the audio track${
                    channel.extractProgress
                      ? ` — ${Math.round(channel.extractProgress * 100)}%`
                      : "…"
                  }`
                : status === "uploading"
                  ? "Uploading…"
                  : status === "transcribing"
                    ? "Transcribing the script…"
                    : status === "analyzing"
                      ? "Claude is analyzing the ad…"
                      : "Waiting in the queue…"}
            </Text>
          </div>
        </div>
      ) : null}

      {result ? (
        <>
          <CopyField label="Headline" value={result.headline} />
          <CopyField label="Primary Text" value={result.primary_text} />
          <CopyField label="Description" value={result.description} />

          {result.notes ? (
            <Alert
              type="warning"
              showIcon
              message="Imperfect fit"
              description={result.notes}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <Collapse
            ghost
            size="small"
            items={[
              {
                key: "analysis",
                label: <Text strong>Why this copy</Text>,
                children: (
                  <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Text>
                      <Text strong>Hook: </Text>
                      {result.analysis?.hook}
                    </Text>
                    <Text>
                      <Text strong>Offer: </Text>
                      {result.analysis?.offer}
                    </Text>
                    <Text>
                      <Text strong>Audience: </Text>
                      {result.analysis?.audience}
                    </Text>
                    <Text>
                      <Text strong>Awareness: </Text>
                      <Tag>{result.analysis?.awareness_stage}</Tag>
                    </Text>
                    <Text>
                      <Text strong>Emotional driver: </Text>
                      {result.analysis?.emotional_driver}
                    </Text>
                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                      {result.analysis?.summary}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Confidence {Math.round((result.confidence || 0) * 100)}%
                    </Text>
                  </Space>
                ),
              },
              ...(channel.transcript
                ? [
                    {
                      key: "transcript",
                      label: <Text strong>Transcript</Text>,
                      children: (
                        <Paragraph
                          style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                          type="secondary"
                        >
                          {channel.transcript}
                        </Paragraph>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {/* The channel: ask follow-ups about THIS ad only. */}
          <div style={{ marginTop: 16, borderTop: "1px solid var(--app-border)", paddingTop: 14 }}>
            <Text
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                opacity: 0.7,
                fontWeight: 600,
              }}
            >
              Channel
            </Text>

            <div
              ref={scrollRef}
              style={{ maxHeight: 260, overflowY: "auto", margin: "10px 0" }}
            >
              {channel.messages.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Ask a follow-up — “why not the other headline?”, “give me a shorter
                  primary text”, “what’s the weakest part of this hook?”
                </Text>
              ) : (
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  {channel.messages.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                        maxWidth: "88%",
                        marginLeft: m.role === "user" ? "auto" : 0,
                        padding: "9px 12px",
                        borderRadius: 10,
                        background:
                          m.role === "user"
                            ? "var(--app-surface-3)"
                            : "var(--app-surface-2)",
                        border: "1px solid var(--app-border)",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.55,
                      }}
                    >
                      {m.content}
                    </div>
                  ))}
                </Space>
              )}
              {channel.chatBusy ? (
                <div style={{ marginTop: 10 }}>
                  <Spin size="small" />
                </div>
              ) : null}
            </div>

            <Space.Compact style={{ width: "100%" }}>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={send}
                placeholder="Ask about this ad…"
                disabled={channel.chatBusy}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={send}
                loading={channel.chatBusy}
              />
            </Space.Compact>
          </div>
        </>
      ) : null}
    </Card>
  );
}

export default function AssignmentPage({ workspaceId, canBulk = true, canEditPrompt = false }) {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [running, setRunning] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [messageApi, contextHolder] = antMessage.useMessage();
  const pasteRef = useRef(null);

  useEffect(() => {
    setAssignmentWorkspaceId(workspaceId);
  }, [workspaceId]);

  const patch = useCallback((id, updates) => {
    setChannels((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    );
  }, []);

  // No size cap on video: only the stripped audio is ever uploaded, so a long
  // video costs a few MB regardless of its source size.
  const addFiles = useCallback((files) => {
    const incoming = Array.from(files || []).filter(
      (f) =>
        String(f.type || "").startsWith("image") ||
        String(f.type || "").startsWith("video"),
    );
    if (!incoming.length) return;
    setFileList((prev) => [...prev, ...incoming]);
  }, []);

  usePasteFiles({
    ref: pasteRef,
    onFiles: addFiles,
    allowedTypes: ["image/", "video/"],
  });

  /** The per-ad pipeline. Runs independently for every channel. */
  const processChannel = useCallback(
    async (channel, { bulk }) => {
      const { id, file } = channel;

      try {
        let images = [];
        let transcript = "";
        let segments = [];

        if (channel.mediaKind === "video") {
          // Video: strip the audio in-browser (a 200 MB video becomes ~5 MB of
          // MP3), upload just that, then transcribe on the GPU. Nothing large
          // is ever uploaded, so video length/size stops mattering.
          patch(id, { status: "extracting" });
          const asset = await uploadForTranscription({
            id,
            file,
            onProgress: (p) => patch(id, { extractProgress: p }),
          });

          patch(id, { status: "transcribing", storagePath: asset.path });
          const t = await transcribeMedia({ storagePath: asset.path });

          transcript = t?.transcript || "";
          segments = t?.segments || [];
          images = []; // audio-only: the model reads the script, not the frames

          if (!transcript) {
            patch(id, {
              status: "error",
              error:
                "No speech was found in that video. The assignment relies on the script, so there's nothing to analyze.",
            });
            return;
          }

          patch(id, { transcript, segments, durationSec: t?.duration || 0 });
        } else {
          // Static: one downscaled frame is the whole ad.
          const part = await fileToImagePart(file);
          images = [{ mime: part.mime, b64: part.b64 }];
          patch(id, { previewUrl: part.previewUrl });
        }

        patch(id, { status: "analyzing", images });

        const res = await analyzeAd({
          mediaKind: channel.mediaKind,
          filename: channel.filename,
          images,
          transcript,
          segments,
          bulk,
        });

        if (res?.refusal) {
          patch(id, { status: "error", error: res.error || "The model declined this ad." });
          return;
        }

        patch(id, { status: "done", result: res.result, images, transcript, segments });
      } catch (error) {
        patch(id, {
          status: "error",
          error: error?.payload?.error || error?.message || "Something went wrong.",
        });
      }
    },
    [patch],
  );

  const run = useCallback(async () => {
    if (!fileList.length) return;

    const bulk = fileList.length > 1;
    if (bulk && !canBulk) {
      messageApi.error(
        "You don't have permission to run bulk analysis. Upload one ad at a time, or ask an admin to enable it.",
      );
      return;
    }

    const fresh = fileList.map((file) => ({
      id: uid(),
      file,
      filename: file.name,
      mediaKind: isVideoFile(file) ? "video" : "image",
      status: "queued",
      error: "",
      previewUrl: "",
      images: [],
      transcript: "",
      segments: [],
      result: null,
      messages: [],
      chatBusy: false,
    }));

    setChannels((prev) => [...fresh, ...prev]);
    setActiveId((cur) => cur || fresh[0].id);
    setFileList([]);
    setRunning(true);

    // Prime with one ad so it writes the master-prompt cache, then fan out —
    // the rest read that cache instead of each paying to write their own.
    await runPool(
      fresh.map((c) => () => processChannel(c, { bulk })),
      { concurrency: 4, prime: fresh.length > 1 },
    );

    setRunning(false);
  }, [fileList, canBulk, messageApi, processChannel]);

  const retry = useCallback(
    (id) => {
      const channel = channels.find((c) => c.id === id);
      if (!channel) return;
      patch(id, { status: "queued", error: "", result: null });
      processChannel(channel, { bulk: false });
    },
    [channels, patch, processChannel],
  );

  const send = useCallback(
    async (id, text) => {
      const channel = channels.find((c) => c.id === id);
      if (!channel) return;

      const history = [...channel.messages, { role: "user", content: text }];
      patch(id, { messages: history, chatBusy: true });

      try {
        const res = await chatWithAd({
          mediaKind: channel.mediaKind,
          filename: channel.filename,
          images: channel.images,
          transcript: channel.transcript,
          segments: channel.segments,
          history: channel.messages,
          message: text,
          priorResult: channel.result,
        });
        patch(id, {
          messages: [...history, { role: "assistant", content: res.reply || "" }],
          chatBusy: false,
        });
      } catch (error) {
        patch(id, {
          messages: [
            ...history,
            {
              role: "assistant",
              content: `⚠️ ${error?.payload?.error || error?.message || "Request failed."}`,
            },
          ],
          chatBusy: false,
        });
      }
    },
    [channels, patch],
  );

  const active = useMemo(
    () => channels.find((c) => c.id === activeId) || null,
    [channels, activeId],
  );

  const done = channels.filter((c) => c.status === "done").length;
  const failed = channels.filter((c) => c.status === "error").length;
  const pct = channels.length
    ? Math.round(((done + failed) / channels.length) * 100)
    : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {contextHolder}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Assignment
          </Title>
          <Text type="secondary">
            Drop ads in bulk. Videos are transcribed, statics are read, and each ad gets
            its headline, primary text, and description assigned from your master prompt.
          </Text>
        </div>
        <Button icon={<SettingOutlined />} onClick={() => setPromptOpen(true)}>
          Master prompt
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card className="panel-card" title="Upload">
            <div ref={pasteRef} tabIndex={0} className="paste-upload-area">
              <Dragger
                multiple
                accept="image/*,video/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  addFiles([file]);
                  return false;
                }}
                fileList={[]}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Drop ads here, or paste</p>
                <p className="ant-upload-hint">Images and videos. Bulk is the point.</p>
              </Dragger>
            </div>

            {fileList.length ? (
              <div style={{ marginTop: 12 }}>
                <div className="post-upload-pills">
                  {fileList.slice(0, 8).map((f, i) => (
                    <span key={i} className="post-upload-pill">
                      {isVideoFile(f) ? <PlayCircleOutlined /> : <PictureOutlined />}{" "}
                      {f.name}
                    </span>
                  ))}
                  {fileList.length > 8 ? (
                    <span className="post-upload-pill post-upload-pill--more">
                      +{fileList.length - 8}
                    </span>
                  ) : null}
                </div>

                <Space style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    loading={running}
                    onClick={run}
                    icon={<FileTextOutlined />}
                  >
                    Analyze {fileList.length} ad{fileList.length === 1 ? "" : "s"}
                  </Button>
                  <Button onClick={() => setFileList([])} disabled={running}>
                    Clear
                  </Button>
                </Space>

                {fileList.length > 1 && !canBulk ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Bulk analysis is disabled for your account. Ask an admin to enable it."
                  />
                ) : null}
              </div>
            ) : null}

            {running ? (
              <Progress percent={pct} size="small" style={{ marginTop: 16 }} />
            ) : null}
          </Card>

          {channels.length ? (
            <Card
              className="panel-card"
              title={`Channels (${channels.length})`}
              style={{ marginTop: 16 }}
              styles={{ body: { padding: 8, maxHeight: 520, overflowY: "auto" } }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                {channels.map((c) => (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveId(c.id)}
                    onKeyDown={(e) => e.key === "Enter" && setActiveId(c.id)}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      padding: 8,
                      borderRadius: 10,
                      cursor: "pointer",
                      border: `1px solid ${
                        c.id === activeId ? "var(--brand-500)" : "transparent"
                      }`,
                      background:
                        c.id === activeId ? "var(--app-surface-2)" : "transparent",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        flexShrink: 0,
                        background: "var(--app-surface-3)",
                        backgroundImage: c.previewUrl ? `url(${c.previewUrl})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {!c.previewUrl &&
                        (c.mediaKind === "video" ? <PlayCircleOutlined /> : <PictureOutlined />)}
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                        }}
                      >
                        {c.filename}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {c.result?.headline || STATUS_META[c.status]?.label}
                      </Text>
                    </div>

                    <Tag color={STATUS_META[c.status]?.color} style={{ marginInlineEnd: 0 }}>
                      {STATUS_META[c.status]?.label}
                    </Tag>
                  </div>
                ))}
              </Space>
            </Card>
          ) : null}
        </Col>

        <Col xs={24} lg={15}>
          <ChannelDetail channel={active} onSend={send} onRetry={retry} />
        </Col>
      </Row>

      <MasterPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        canEdit={canEditPrompt}
      />
    </Space>
  );
}
